import { useEffect, useState, useRef, useCallback } from "react";
import Xarrow from "react-xarrows";
import { v4 as uuidv4 } from "uuid";
import { t } from "@lingui/core/macro";
import toast, { Toaster } from "react-hot-toast";
import Drawer from "react-modern-drawer";
import Marker from "./components/Marker";
import DrawerContent from "./components/DrawerContent.js";
import {
  getW3wInfoByCoordinates,
  getCurrentMarkersList,
  getCurrentMarkersCategoryList,
  isUserLoggedIn,
  isValidMarkerId,
} from "./components/Utility.js";
import { retrieveUserLogin } from "./components/Api.js";
import DrawerContentRead from "./components/DrawerContentRead.js";
import Navbar from "./components/Navbar.js";
import AutocompleteSearch from "./components/AutocompleteSearch.js";
import "./App.css";
import "react-modern-drawer/dist/index.css";

/**
 * A component that displays the entire application
 *
 * @param {string} props.idInit - A story id to immediately view the story card 
 * @param {object} props.initPosition - A default lat long initial map position
 * @returns {JSX.Element}
 */

function App({
  idInit,
  initPosition = { lat: 39.67341563744307, lng: 8.886219610177024 },
}) {

  const [map, setMap] = useState();
  const mapBox = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [isEditingDrawer, setIsEditingDrawer] = useState(false);
  const [clickedMarker, setClickedMarker] = useState({});
  const startPosition = initPosition;
  const [currentMarkersList, setCurrentMarkersList] = useState([]);
  const [currentKey, setCurrentKey] = useState("jd73y3jedj78");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isOpenSearchDrawer, setIsOpenSearchDrawer] = useState(false);
  const [currentCategoryFilter, setCurrentCategoryFilter] = useState("");
  const currentCategoryFilterRef = useRef("");


  /**
   * A function that manages the opening of the story drawer
   * @param {object} param - A story data (id)
   * @returns {void} Assign some state variables 
   */
  const toggleDrawer = useCallback(
    (param) => {
      if (isOpen) {
        setCurrentKey(uuidv4());
      }
      setIsOpen((prevState) => !prevState);
      //if current user is the owner, then setiseditingdrawer to true, to edit it
      setIsEditingDrawer(false);
      if (param && !isOpen) {
        setClickedMarker(param);
      }
    },
    []
  );

  /**
   * A function that manages the opening of the search drawer
   * @param {object} param - A story data (id)
   * @returns {void} Assign some state variables 
   */
  const toggleDrawerSearch = (param) => {
    setIsOpenSearchDrawer((prevState) => !prevState);
    if (!isOpenSearchDrawer) {
      setIsOpenSearchDrawer(true);
    }
  };

  /**
   * A function that manages the opening of the edit drawer
   * @param {object} param - A story data (id)
   * @returns {void} Assign some state variables 
   */
  const toggleDrawerEditing = (param) => {
    setIsOpen((prevState) => !prevState);
    if (!isOpen) {
      setClickedMarker(param);
      setIsEditingDrawer(true);
    }
  };

  /**
   * A function that call a drawer toggling for read or edit a story
   * @param {object} data - A story data
   * @returns {void} Call a toggle function 
   */
  const handleClickMarker = (data) => {
    console.log(currentUserId);
    if (currentUserId !== data.userID) toggleDrawer({ id: data._id });
    else if (isUserLoggedIn()) {
      toggleDrawerEditing({
        id: data._id,
        lat: data.position.coordinates[1],
        lng: data.position.coordinates[0],
        w3wData: data.w3wData,
      });
    }
  };

  /**
   * A function that manages the opening of the drawer
   * @param {object} event - An event object
   * @returns {void} Call a filtering search map function 
   */
  const handleCategoryChange = (event) => {
    if (event.target.value) {
      filterCategorySearchMap(event.target.value);
    }
  };

  /**
 * A function that changes the position and zoom of the map
 * @param {object} story - A story object
 * @returns {void} Call the function for setting the position and zoom
 */
  const editOwnMap = (story) => {
    const position = {
      lat: story.position.coordinates[1],
      lng: story.position.coordinates[0],
    };
    map.setCenter(position);
    map.setZoom(16);
  };

  /**
   * A function that manages map refresh and the current list of stories 
   * @param {searchObj} event - A searched story
   * @returns {void} Set the current map center and call a function to get the current list of stories
   */
  const editSearchMap = (searchObj) => {
    const position = {
      lat: searchObj.position.lat,
      lng: searchObj.position.lng,
    };
    map.setCenter(position);
    getCurrentMarkersList(position.lat, position.lng, 20000).then((data) => {
      if (data.success && Object.keys(data.data).length > 0) {
        setCurrentMarkersList(data.data);
      }
    });
    map.setZoom(16);
  };

  /**
   * A function that manages the display of a specific category of stories
   * @param {string} category - A story category
   * @returns {void} Call a function that obtains the list of stories filtered by category
   */
  const filterCategorySearchMap = (category) => {
    setCurrentCategoryFilter(category);
    document.querySelector(".reset-map-control-select").style.display =
      "initial";
    getCurrentMarkersCategoryList(category, 5).then((data) => {
      if (data.success && Object.keys(data.data).length > 0) {
        setCurrentMarkersList(data.data);
        document.querySelector(".reset-map-control-select").style.display =
          "initial";
      }
    });
  };

  /**
   * A function that refresh the map based on the logging status
   * @returns {void} Assign some state variables
   */
  const refreshOwnMap = () => {
    retrieveUserLogin()
      .then((data) => {
        console.log(data);
        if (data.success) {
          //set current login info cookie
          const { userId } = data.data;
          setCurrentUserId(userId);
        } else {
          setCurrentUserId(null);
        }
      })
      .catch((error) => console.error(error));
  };

  /**
   * A function refresh the map with default parameters
   * @param {number} lat - A latitude value
   * @param {number} lng - A longitued value
   * @returns {void} Assign some state variables
   */
  const restoreMapDefault = (lat, lng) => {
    getCurrentMarkersList(lat, lng, 20000).then((data) => {
      if (data.success) {
        setCurrentMarkersList(data.data);
      }
    });
  };

  /**
   * A function that close the drawer and refresh the map with default parameters
   * @returns {void} Close the drawer and refresh the map
   */
  const closeAfterEditing = () => {
    toggleDrawer();
    restoreMapDefault(map.getCenter().lat(), map.getCenter().lng());
  };

  // set a default map options obj
  const mapOptions = {
    center: startPosition,
    zoom: 16,
    streetViewControl: false,
    mapId: "142a529fd8f26d4b5abc434e",
    mapTypeControl: true,
    fullscreenControl: false,
    mapTypeControlOptions: {
      style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
      position: window.google.maps.ControlPosition.TOP_RIGHT,
    },
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },

    ],
    /* mapTypeId: 'satellite', */
  };

  /**
   * A function that show an error in case of google geolocation service failure
   * @param {boolean} browserHasGeolocation - A boolean value
   * @param {object} infoWindow - An infoWindow google maps object
   * @param {object} pos - A long/lat object
   * @returns {void} Call some infoWindow methods
   */
  // 
  const handleLocationError = (browserHasGeolocation, infoWindow, pos) => {
    infoWindow.setPosition(pos);
    infoWindow.setContent(
      browserHasGeolocation
        ? "Error: The Geolocation service failed."
        : "Error: Your browser doesn't support geolocation."
    );
    infoWindow.open(map);
  };

  // Update the ref when a current categ filter state changes
  useEffect(() => {
    currentCategoryFilterRef.current = currentCategoryFilter;
  }, [currentCategoryFilter]);

  // Set an initial google map with features
  useEffect(() => {
    setMap(new window.google.maps.Map(mapBox.current, mapOptions));
    getCurrentMarkersList(startPosition.lat, startPosition.lng, 30000).then(
      (data) => {
        if (data.success && Object.keys(data.data).length > 0) {
          setCurrentMarkersList(data.data);
        }
      }
    );

    // Call a function to obtain any login data
    retrieveUserLogin()
      .then((data) => {
        console.log(data);
        if (data.success) {
          //set current login info cookie
          const { userId } = data.data;
          setCurrentUserId(userId);
        } else {
          setCurrentUserId(null);
        }
      })
      .catch((error) => console.error(error));
    // eslint-disable-next-line
  }, []);

  // When the map was loaded, use listeners on it
  useEffect(() => {
    if (map) {
      const glyphImg = document.createElement("img");
      glyphImg.src = "/target-svgrepo-com.svg";
      glyphImg.className = "add-info-button";
      glyphImg.id = "add-interest-button-center";
      const markerViewWithText =
        new window.google.maps.marker.AdvancedMarkerElement({
          map,
          position: startPosition,
          title: "Inserisci una nota in questo punto!",
          gmpClickable: true,
          content: glyphImg,
        });
      markerViewWithText.content.style.transform = "translateY(50%)";
     /* const infoWindow = new window.google.maps.InfoWindow();

       markerViewWithText.addListener("click", ({ domEvent, latLng }) => {
        infoWindow.close();
        const addPointButton = document.createElement("button");
        addPointButton.textContent = t`Add a story`;
        addPointButton.classList.add("custom-map-control-button");
        addPointButton.addEventListener("click", () => {
          //check if authenticated
          if (!isUserLoggedIn()) {
            toast(t`You must log in first!`, {
              icon: "🥵",
            });
            return;
          }
          let latCenterClick = map.getCenter().lat();
          let lngCenterClick = map.getCenter().lng();
          //get w3w data
          let w3wInfo = getW3wInfoByCoordinates(latCenterClick, lngCenterClick);
          w3wInfo
            .then((result) => {
              toggleDrawerEditing({
                lat: latCenterClick,
                lng: lngCenterClick,
                w3wData: {
                  address: result.words,
                  idW: result.words,
                },
              });
            })
            .catch((err) => {
              console.log(err);
            });
        });
        infoWindow.setContent(addPointButton);
        infoWindow.open(markerViewWithText.map, markerViewWithText);
      }); */

      // add a listener when the map center changed
      map.addListener("center_changed", () => {
        let latCenter = map.getCenter().lat();
        let lngCenter = map.getCenter().lng();
        let newLatLng = new window.google.maps.LatLng(latCenter, lngCenter);
        markerViewWithText.position = newLatLng;
      });

      // add a listener when the drag action end
      map.addListener("dragend", () => {
        //get w3w words
        let latCenterClick = map.getCenter().lat();
        let lngCenterClick = map.getCenter().lng();
        //get w3w data
        let w3wInfo = getW3wInfoByCoordinates(latCenterClick, lngCenterClick);
        w3wInfo
          .then((result) => {
            document.getElementById('w3wtext-div').style.display = 'block';
            document.getElementById('w3wtext-span').innerHTML = '<a target="_blank" href="https://what3words.com/' + result.words + '">///' + result.words + '</a>';
          })
          .catch((err) => {
            console.log(err);
          });


        if (currentCategoryFilterRef.current) {
        } else {
          getCurrentMarkersList(
            map.getCenter().lat(),
            map.getCenter().lng(),
            20000
          ).then((data) => {
            if (data.success) {
              setCurrentMarkersList(data.data);
            }
          });
        }
      });

      // Add the what3words grid to the Google Map data layer once the desired zoom level is meet
      let gridData;
      map.addListener("bounds_changed", function () {
        const zoom = map.getZoom();
        const loadFeatures = zoom > 17;

        if (loadFeatures) {
          // Zoom level is high enough
          var ne = map.getBounds().getNorthEast();
          var sw = map.getBounds().getSouthWest();

          // Call the what3words Grid API to obtain the grid squares within the current visble bounding box
          window.what3words.api
            .gridSection({
              boundingBox: {
                southwest: {
                  lat: sw.lat(),
                  lng: sw.lng(),
                },
                northeast: {
                  lat: ne.lat(),
                  lng: ne.lng(),
                },
              },
              format: "geojson",
            })
            .then(function (data) {
              if (gridData !== undefined) {
                for (var i = 0; i < gridData.length; i++) {
                  map.data.remove(gridData[i]);
                }
              }
              gridData = map.data.addGeoJson(data);
            })
            .catch(console.error);
        }

        // Set the grid display style
        map.data.setStyle({
          visible: loadFeatures,
          strokeColor: "#777",
          strokeWeight: 0.5,
        });
      });

      // Add current location feature
      const infoWindowGeo = new window.google.maps.InfoWindow();

      // Add custom button for map geolocalization
      const locationButton = document.createElement("button");
      locationButton.textContent = "";
      locationButton.title = t("Center map");
      locationButton.classList.add("center-map-control-button");
      map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(
        locationButton
      );
      locationButton.addEventListener("click", () => {
        // Try HTML5 geolocation if available
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const pos = {
                lat: parseFloat(position.coords.latitude),
                lng: parseFloat(position.coords.longitude),
              };

              map.setCenter(pos);
              map.setZoom(20);
            },
            () => {
              handleLocationError(true, infoWindowGeo, map.getCenter());
            }
          );
        } else {
          // Browser doesn't support Geolocation
          handleLocationError(false, infoWindowGeo, map.getCenter());
        }
      });

      // Button to add a new point of interest
/*       const interestButton = document.createElement("button");
      interestButton.id = "add-interest-button-top";
      interestButton.textContent = t`Add a story`;
      interestButton.classList.add("custom-map-control-button");
 */
      const interestButton2 = document.createElement("button");
      interestButton2.id = "add-interest-button-top2";
      interestButton2.textContent = t`Search`;
      interestButton2.classList.add("custom-map-control-button");
      interestButton2.addEventListener("click", toggleDrawerSearch);

      // Select to filter by category
      const interestButton3 = document.createElement("select");
      interestButton3.id = "add-interest-button-top3";
      interestButton3.classList.add("custom-map-control-select");
      interestButton3.add(new Option(t`Choose a category`, ""));
      interestButton3.add(
        new Option(t`Archaeological site`, "Archaeological site")
      );
      interestButton3.add(
        new Option(t`Catholic church`, "Catholic church")
      );
      interestButton3.add(
        new Option(t`Museum`, "Museum")
      );
      interestButton3.add(
        new Option(t`Tourist attraction`, "Tourist attraction")
      );
      interestButton3.addEventListener("change", handleCategoryChange);
      const interestButtonReset = document.createElement("button");
      interestButtonReset.classList.add("reset-map-control-select");
      interestButtonReset.addEventListener("click", (event) => {
        interestButton3.selectedIndex = 0;
        event.currentTarget.style.display = "none";
        setCurrentCategoryFilter(null);
        restoreMapDefault(map.getCenter().lat(), map.getCenter().lng());
      });

      // Add a div element that display current w3w map word
/*       const w3wText = document.createElement("span");
      w3wText.classList.add("custom-map-w3wtext-span");
      w3wText.id = "w3wtext-span"; */
      const selectWrapper = document.createElement("div");
      selectWrapper.appendChild(interestButton3);
      selectWrapper.appendChild(interestButtonReset);
      const interestWrapper = document.createElement("div");
      interestWrapper.classList.add("custom-map-control-container");
      interestWrapper.appendChild(interestButton2);
      interestWrapper.appendChild(selectWrapper);
      //interestWrapper.appendChild(interestButton);

      map.controls[window.google.maps.ControlPosition.TOP_LEFT].push(
        interestWrapper
      );

      const w3wLabelWrapper = document.createElement("div");
      w3wLabelWrapper.classList.add("custom-w3wlabel-container");
      w3wLabelWrapper.id = "w3wtext-div";
      //w3wLabelWrapper.appendChild(w3wText);
      map.controls[window.google.maps.ControlPosition.BOTTOM_CENTER].push(
        w3wLabelWrapper
      );
  /*     interestButton.addEventListener("click", () => {
        // Check if authenticated
        if (!isUserLoggedIn()) {
          toast(t`You must log in first!`, {
            icon: "🥵",
          });
          return;
        }
        let latCenterClick = map.getCenter().lat();
        let lngCenterClick = map.getCenter().lng();
        // Get w3w data
        let w3wInfo = getW3wInfoByCoordinates(latCenterClick, lngCenterClick);
        w3wInfo
          .then((result) => {
            toggleDrawerEditing({
              lat: latCenterClick,
              lng: lngCenterClick,
              w3wData: {
                address: result.words,
                idW: result.words,
              },
            });
          })
          .catch((err) => {
            console.log(err);
          });
      }); */

      // If the map is initialized with an id, then open the story by id
      if (isValidMarkerId(idInit)) {
        toggleDrawer({ id: idInit });
      }
    }
    // eslint-disable-next-line
  }, [map]);

  return (
    <div className="App">
      <header className="App-header">
        <a href="https://siddialtipiano.it/" className="Homeback-header">
          <img alt="Siddi Logo" src="/home-1-svgrepo-com.svg" />
          <span>Home</span>
        </a>
        <p>Siddi – Maps</p>
        {/*<Navbar updateMap={editOwnMap} refreshOwnMap={refreshOwnMap} />*/}
      </header>
      {/* Show the search drawer */}
      <Drawer
        open={isOpenSearchDrawer}
        onClose={toggleDrawerSearch}
        direction="top"
        className="search-map-container"
      >
        <AutocompleteSearch
          map={map}
          updateMap={editSearchMap}
          onFilterCategorySearchMap={filterCategorySearchMap}
        />
      </Drawer>
      <div id="map" ref={mapBox}></div>
      {document.getElementById("add-interest-button-center") &&
        document.getElementById("add-interest-button-top") && (
          <Xarrow
            start={"add-interest-button-center"} //can be react ref
            end={"add-interest-button-top"} //or an id
            lineColor="greenyellow"
            headColor="greenyellow"
            dashness={true}
            curveness={0.4}
          />
        )}
      {currentMarkersList &&
        currentMarkersList.map((aData, index) => (
          <Marker
            key={"raw_" + aData._id}
            id={"raw_" + aData._id}
            map={map}
            position={{
              lat: aData.position.coordinates[1],
              lng: aData.position.coordinates[0],
            }}
            onClick={() => handleClickMarker(aData)}
            title=""
            isOwner={
              isUserLoggedIn() && currentUserId === aData.userID ? true : false
            }
          ></Marker>
        ))}

      <>
        {/* Displays a drawer for viewing or editing a story */}
        <Drawer
          open={isOpen}
          onClose={toggleDrawer}
          direction="bottom"
          duration={600}
          overlayOpacity={0.7}
          overlayColor="#000000b3"
          enableOverlay={true}
          className="drawer-box"
          size={isEditingDrawer ? "93vh" : "55vh"}
        >
          {console.log("APP re-renderizzato")}
          {isEditingDrawer ? (
            <DrawerContent
              key={`${currentKey}_edit`}
              markerInUse={clickedMarker}
              editable={isEditingDrawer}
              closeAll={closeAfterEditing}
            ></DrawerContent>
          ) : (
            <DrawerContentRead
              key={currentKey}
              markerInUse={clickedMarker}
            ></DrawerContentRead>
          )}
        </Drawer>
      </>
      {/* A toaster to display useful messages*/}
      <Toaster position="bottom-center" />
    </div>
  );
}

export default App;
